import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Mapa com pino arrastável para o cliente confirmar onde a entrega deve chegar.
 *
 * O mapa é do OpenStreetMap, não do Google, de propósito: desenhar o mapa no
 * navegador exigiria a chave do Google no HTML, e a chave da loja é justamente
 * a que NÃO tem restrição de referrer (para funcionar no servidor) — exposta na
 * página, qualquer um gastaria a conta do lojista. O Google continua fazendo o
 * que importa para o dinheiro: geocodificar e medir a rota, no backend.
 *
 * O que sai daqui é a coordenada; a precisão da taxa vem do Directions depois.
 */

/** Ícone padrão do Leaflet quebra com bundler (usa caminhos relativos). SVG inline resolve. */
const PINO = L.divIcon({
  className: '',
  html: `<svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 0C7.2 0 0 7.2 0 16c0 11 16 26 16 26s16-15 16-26c0-8.8-7.2-16-16-16z" fill="#E91E63"/>
    <circle cx="16" cy="16" r="6" fill="#fff"/>
  </svg>`,
  iconSize: [32, 42],
  iconAnchor: [16, 42],
});

interface Props {
  latitude: number;
  longitude: number;
  aoMover: (latitude: number, longitude: number) => void;
  altura?: number;
}

export function MapaPino({ latitude, longitude, aoMover, altura = 320 }: Props): JSX.Element {
  const divRef = useRef<HTMLDivElement>(null);
  const mapaRef = useRef<L.Map | null>(null);
  const pinoRef = useRef<L.Marker | null>(null);
  // A prop muda a cada arrasto (o pai guarda a coordenada); sem a ref o efeito
  // recriaria o mapa a cada movimento.
  const aoMoverRef = useRef(aoMover);
  aoMoverRef.current = aoMover;

  useEffect(() => {
    if (!divRef.current || mapaRef.current) return;

    const mapa = L.map(divRef.current, { zoomControl: true, attributionControl: true }).setView(
      [latitude, longitude],
      17,
    );
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(mapa);

    const pino = L.marker([latitude, longitude], { draggable: true, icon: PINO }).addTo(mapa);
    pino.on('dragend', () => {
      const p = pino.getLatLng();
      aoMoverRef.current(p.lat, p.lng);
    });
    // Tocar no mapa também move o pino — no celular é mais fácil que arrastar.
    mapa.on('click', (e: L.LeafletMouseEvent) => {
      pino.setLatLng(e.latlng);
      aoMoverRef.current(e.latlng.lat, e.latlng.lng);
    });

    mapaRef.current = mapa;
    pinoRef.current = pino;

    // O mapa nasce dentro de um diálogo que ainda está animando; sem isto ele
    // calcula o tamanho errado e aparece cinza pela metade.
    const t = setTimeout(() => mapa.invalidateSize(), 250);
    return () => {
      clearTimeout(t);
      mapa.remove();
      mapaRef.current = null;
      pinoRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coordenada trocada por fora (ex.: "usar minha localização"): recentra.
  useEffect(() => {
    if (!mapaRef.current || !pinoRef.current) return;
    const atual = pinoRef.current.getLatLng();
    if (Math.abs(atual.lat - latitude) < 1e-7 && Math.abs(atual.lng - longitude) < 1e-7) return;
    pinoRef.current.setLatLng([latitude, longitude]);
    mapaRef.current.setView([latitude, longitude], mapaRef.current.getZoom());
  }, [latitude, longitude]);

  return <Box ref={divRef} sx={{ height: altura, width: '100%', borderRadius: 1, overflow: 'hidden' }} />;
}
